import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import PubSub from "../../../services/PubSub";
import Icon from "../Icon";
import CreateModal from "./CreateModal";

const fileRegex = /(.*?)\.(json|js|scss|css)$/;
const folderRegex = /^[a-zA-Z0-9_-]+$/;

const FilePicker = ({
    files,
    basePath = "",
    configKey,
    onSelectFile,
    onAddFile,
    onDeleteFile,
    editable = true,
    showIcons = true,
    allowFolderCreation = true,
    fixedFiles = []
}) => {
    const prevConfigKey = useRef();
    const [translate] = useTranslation();

    const [selectedNode, setSelectedNode] = useState("");
    const [drillDownPath, setDrillDownPath] = useState("/");
    const [displayData, setDisplayData] = useState([]);
    const [temporaryFolders, setTemporaryFolders] = useState([]);

    const [showCreateModal, setShowCreateModal] = useState(null);

    const displayDrilldown = (path) => {
        const subPaths = path.split('/');

        return subPaths.length > 4 ? `.../${subPaths[subPaths.length - 3]}/${subPaths[subPaths.length - 2]}/` : path;
    }

    const isNode = (path) => {
        return path.split('/').length === 1;
    }

    const compareNodes = (a, b) => {
        if (a.isFolder === true && b.isFolder === false) {
            return -1
        }
        if (a.isFolder === false && b.isFolder === true) {
            return 1;
        }

        return 0;
    }

    const handleNavigation = useCallback((nextFile, nextDrillDownPath) => {
        setSelectedNode(nextFile);
        if (nextDrillDownPath) {
            setDrillDownPath(nextDrillDownPath);
        }

        onSelectFile(nextFile !== "" ? basePath + nextDrillDownPath + nextFile : "");
    }, [onSelectFile, basePath])

    const handleBackClick = useCallback(() => {
        handleNavigation("", drillDownPath.split('/').slice(0, -2).reduce((acc, data) => acc + '/' + data) + '/');
    }, [handleNavigation, drillDownPath]);

    const handleAddFile = useCallback((fileName) => {
        const filePath = `${basePath}${drillDownPath}${fileName}`;
        if (files.includes(filePath)) {
            PubSub.publish("show_info", "OTM_FILE_EXISTS");
            return;
        }

        onAddFile(filePath);
        setShowCreateModal(null);
    }, [onAddFile, files, basePath, drillDownPath]);

    const handleDeleteFile = useCallback((fileName) => {
        onDeleteFile(`${basePath}${drillDownPath}${fileName}`);
    }, [basePath, drillDownPath, onDeleteFile]);

    const handleAddFolder = useCallback((folderName) => {
        const filePath = `${basePath}${drillDownPath}${folderName}`;
        for (var i = 0; i < displayData.length; i++) {
            if (displayData[i].isFolder && displayData[i].node === folderName) {
                PubSub.publish("show_info", "OTM_FOLDER_EXISTS");
                return;
            }
        }

        setTemporaryFolders(data => { data.push(filePath); return [...data]; });
        setShowCreateModal(null);
    }, [displayData, basePath, drillDownPath]);

    const handleDeleteFolder = useCallback((folderName) => {
        const filePath = `${basePath}${drillDownPath}${folderName}`;
        for (var i = 0; i < files.length; i++) {
            if (files[i].includes(filePath)) {
                PubSub.publish("show_info", "OTM_FOLDER_NOT_EMPTY");
                return;
            }
        }

        for (i = 0; i < temporaryFolders.length; i++) {
            if (temporaryFolders[i].startsWith(filePath) && temporaryFolders[i] !== filePath) {
                PubSub.publish("show_info", "OTM_FOLDER_NOT_EMPTY");
                return;
            }
        }

        setTemporaryFolders(data => { data.splice(data.indexOf(filePath), 1); return [...data] })
    }, [files, basePath, drillDownPath, temporaryFolders, setTemporaryFolders]);

    useEffect(() => {
        if (prevConfigKey.current !== configKey) {
            handleNavigation("", "/");
            setTemporaryFolders([]);
        }
        prevConfigKey.current = configKey;
    }, [configKey, handleNavigation]);

    useEffect(() => {
        if (drillDownPath && files) {
            const workingFiles = files.map(file => file.replace(basePath, ""));
            const paths = workingFiles.filter(path => path.startsWith(drillDownPath));
            setDisplayData(() => {
                const returnData = [];
                const existingPaths = [];
                for (var i = 0; i < paths.length; i++) {
                    if (isNode(paths[i].replace(drillDownPath, ""))) {
                        returnData.push({ node: paths[i].replace(drillDownPath, ""), isFolder: false })
                    }
                    else {
                        const nodeValue = paths[i].replace(drillDownPath, "").split('/')[0];
                        if (!existingPaths.includes(nodeValue)) {
                            existingPaths.push(nodeValue);
                            returnData.push({ node: nodeValue, isFolder: true })
                        }
                    }
                }

                for (i = 0; i < temporaryFolders.length; i++) {
                    const nodeValue = temporaryFolders[i].replace(basePath + drillDownPath, "").split('/')[0];
                    if (nodeValue !== "" && !existingPaths.includes(nodeValue)) {
                        existingPaths.push(nodeValue);
                        returnData.push({ node: nodeValue, isFolder: true })
                    }
                }

                return returnData.sort(compareNodes);
            });
        }
    }, [drillDownPath, files, basePath, temporaryFolders]);

    const renderFile = useCallback((key, index) => {
        return <li
            className={`${selectedNode === key.node ? "active" : ""}  list-group-item border-0 rounded-0 px-2 p-0 justify-content-between d-flex`}
            key={index}>
            <div
                style={{ lineHeight: "24px" }}
                onClick={() => handleNavigation(key.node, drillDownPath)}
                className="clickable">
                <span className={`${selectedNode === key.node ? "" : "text-muted"}`}>
                    {showIcons && <Icon icon={"Description"} className="icon-small" />}
                </span>
                {key.node}
            </div>
            {editable && !fixedFiles.includes(basePath + drillDownPath + key.node) && <Icon
                icon={"DeleteOutline"}
                className={`clickable ${selectedNode === key.node ? "" : "text-muted"}`}
                onClick={() => handleDeleteFile(key.node)} />}
        </li>
    }, [selectedNode, drillDownPath, showIcons, editable, fixedFiles, basePath, handleDeleteFile, handleNavigation]);

    const renderFolder = useCallback((key, index) => {
        return <li className="list-group-item border-0 rounded-0 p-0 px-2 justify-content-between d-flex"
            key={index}>
            <span
                className="text-muted fw-bold clickable pe-2"
                style={{ lineHeight: "24px" }}
                onClick={() => handleNavigation("", drillDownPath + key.node + '/')}>
                {showIcons && <Icon icon={"Folder"} className="icon-small" />}
                {key.node}
            </span>
            {editable && <Icon
                icon={"DeleteOutline"}
                className="clickable text-muted"
                onClick={() => handleDeleteFolder(key.node)} />}
        </li>
    }, [drillDownPath, showIcons, editable, handleDeleteFolder, handleNavigation]);

    const renderTree = useMemo(() => {
        return <ul className="list-group mt-2 fileTree">
            <li className="list-group-item border-0 rounded-0 p-0 px-2 text-muted">
                <div className="row fw-bold"  >
                    <div className="col-auto">{showIcons && <Icon icon={"Folder"} />}</div>
                    <div className="col p-0 m-0" style={{ lineHeight: "22px" }}>
                        {displayDrilldown(basePath + drillDownPath)}
                    </div>

                </div>
            </li>
            <li className="list-group-item border-0 rounded-0 p-0 px-2 text-muted">
                <div className="row justify-content-between">
                    <div className="col-1">
                        <Icon
                            icon={"KeyboardBackspace"}
                            className={`${drillDownPath === '/' ? 'disabled' : 'clickable'}`}
                            onClick={handleBackClick} />
                    </div>
                    {editable && <div className="col-auto">
                        <Icon
                            icon={"CreateNewFolder"}
                            className={allowFolderCreation ? "clickable" : "disabled"}
                            onClick={() => setShowCreateModal("folder")} />
                        <Icon
                            icon={"NoteAdd"}
                            className="clickable"
                            onClick={() => setShowCreateModal("file")} />
                    </div>}
                </div>
            </li>
            {displayData.length === 0 ?
                <li className="list-group-item border-0 rounded-0 p-0 px-2 text-muted">
                    {translate("OTM_NO_FILES")}
                </li>
                :
                displayData.map((key, index) => key.isFolder ? renderFolder(key, index) : renderFile(key, index))
            }
        </ul >
    }, [displayData, drillDownPath, editable, showIcons, allowFolderCreation, renderFile, renderFolder, basePath, handleBackClick, translate]);

    return (
        <>
            <CreateModal
                show={showCreateModal === "file"}
                heading={"OTM_CREATE_FILE"}
                validationRegex={fileRegex}
                onCreate={handleAddFile}
                onCancel={() => setShowCreateModal(null)} />
            <CreateModal
                show={showCreateModal === "folder"}
                heading={"OTM_CREATE_FOLDER"}
                validationRegex={folderRegex}
                onCreate={handleAddFolder}
                onCancel={() => setShowCreateModal(null)} />
            <div className="container px-0 fs-7" >
                {renderTree}
            </div>
        </>
    );
};

export default FilePicker;